import React from 'react';
import { T, Var, useGT } from 'gt-react';
import type {
  Sd844EmailTemplate,
  Sd844TemplateSampleSource,
  Sd844TemplateSheetPreview,
} from '@erp/rpc';
import { Checkbox } from 'src/components/ui/checkbox';
import { Button } from 'src/components/ui/button';
import { Badge } from 'src/components/ui/badge';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import {
  DownloadSimpleIcon,
  PowerIcon,
  SpinnerIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { Sd844TemplateColumnMapper } from './Sd844TemplateColumnMapper';
import type {
  PendingTemplateUpload,
  TemplateDraft,
} from './sd844TemplateEditorState';
import { exclusionCandidates } from './sd844TemplateEditorState';
import type { Sd844TemplateEditorController } from 'src/api/rpc/hooks/ship-and-debit/useSd844TemplateEditor';

/**
 * The body of the 844 template editor: which file, which sheet, where the rows
 * start, the bindings, and the save / preview / remove actions.
 *
 * Separate from the editor itself so the editor is about getting a file (upload,
 * empty states, mutations) and this is about what to do with one.
 */

interface Props {
  /** The form being shown — the editor only renders this with one. */
  form: TemplateDraft;
  editor: Sd844TemplateEditorController;
}

export function Sd844TemplateForm({ form, editor }: Props) {
  const { pendingUpload } = editor;
  const readOnly = !editor.canEdit;
  const otherSheets = exclusionCandidates(
    pendingUpload,
    editor.template,
    form.excludedSheetNames,
  );

  return (
    <>
      <TemplateFileLine
        template={editor.template}
        pendingUpload={pendingUpload}
      />

      {editor.template && (
        <TemplateActivation template={editor.template} editor={editor} />
      )}

      <TemplateFileFields
        form={form}
        pendingUpload={pendingUpload}
        onSelectSheet={editor.selectSheet}
        onChangeDataStartRow={editor.setDataStartRow}
        readOnly={readOnly}
      />

      <ExcludedSheets
        claimSheet={form.sheetName}
        sheetNames={otherSheets.filter((name) => name !== form.sheetName)}
        excluded={form.excludedSheetNames}
        onToggle={editor.setExcludedSheet}
        readOnly={readOnly}
      />

      <SuggestionSummary
        isSuggesting={editor.isSuggesting}
        suggestedCount={editor.suggestedCount}
        unresolvedCount={editor.unresolved.size}
        uncertainCount={editor.uncertain.size}
      />

      <SampleSourcePicker
        source={editor.sampleSource}
        onChange={editor.setSampleSource}
        invoices={editor.sampleInvoices}
        isLoading={editor.isLoadingSample}
      />

      <Sd844TemplateColumnMapper
        mappings={form.mappings}
        onChangeSource={editor.setColumnSource}
        unresolved={editor.unresolved}
        isSuggesting={editor.isSuggesting}
        reasons={editor.reasons}
        uncertain={editor.uncertain}
        sampleValues={editor.sampleValues}
        hasSample={editor.sampleInvoices !== null}
        readOnly={readOnly}
      />

      <TemplateActions form={form} editor={editor} />
    </>
  );
}

function TemplateActivation({
  template,
  editor,
}: {
  template: Sd844EmailTemplate;
  editor: Sd844TemplateEditorController;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border p-2 text-xs">
      <ActivationStatus isEnabled={template.isEnabled} />
      {editor.canEdit && (
        <ActivationButton isEnabled={template.isEnabled} editor={editor} />
      )}
    </div>
  );
}

function ActivationStatus({ isEnabled }: { isEnabled: boolean }) {
  if (isEnabled) {
    return (
      <>
        <Badge>
          <T context="badge: saved 844 template is enabled">Enabled</T>
        </Badge>
        <span className="text-muted-foreground flex-1">
          <T context="explains that an enabled 844 template is used for future claims">
            Future email claims use this template.
          </T>
        </span>
      </>
    );
  }
  return (
    <>
      <Badge variant="outline">
        <T context="badge: saved 844 template is disabled">Disabled</T>
      </Badge>
      <span className="text-muted-foreground flex-1">
        <T context="explains that a disabled 844 template is skipped for future claims">
          Future email claims use the fallback template while this is disabled.
        </T>
      </span>
    </>
  );
}

function ActivationButton({
  isEnabled,
  editor,
}: {
  isEnabled: boolean;
  editor: Sd844TemplateEditorController;
}) {
  if (isEnabled) {
    return (
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={editor.isBusy}
        onClick={() => void editor.setEnabled(false)}
      >
        {editor.isChangingEnabled ? (
          <SpinnerIcon className="size-4 animate-spin" />
        ) : (
          <PowerIcon className="size-4" />
        )}
        <T context="button that disables an 844 email template">Disable</T>
      </Button>
    );
  }
  return (
    <Button
      type="button"
      size="sm"
      disabled={
        editor.isBusy || editor.hasUnsavedEdits || editor.uncertain.size > 0
      }
      onClick={() => void editor.setEnabled(true)}
    >
      {editor.isChangingEnabled ? (
        <SpinnerIcon className="size-4 animate-spin" />
      ) : (
        <PowerIcon className="size-4" />
      )}
      <T context="button that enables an 844 email template">Enable</T>
    </Button>
  );
}

/** Which file is in use, and who put it there. */
function TemplateFileLine({
  template,
  pendingUpload,
}: {
  template: Sd844EmailTemplate | null;
  pendingUpload: PendingTemplateUpload | null;
}) {
  const filename = pendingUpload?.filename ?? template?.originalFilename ?? '';
  const uploadedBy = pendingUpload ? null : template?.uploadedByName;

  return (
    <p className="text-xs">
      <T context="shows which file is in use as the 844 template">
        File: <Var>{filename}</Var>
      </T>
      {uploadedBy && (
        <span className="text-muted-foreground">
          {' — '}
          <T context="who uploaded the current 844 template">
            uploaded by <Var>{uploadedBy}</Var>
          </T>
        </span>
      )}
    </p>
  );
}

/** Which sheet takes the rows, and the row they start on. */
function TemplateFileFields({
  form,
  pendingUpload,
  onSelectSheet,
  onChangeDataStartRow,
  readOnly,
}: {
  form: TemplateDraft;
  pendingUpload: PendingTemplateUpload | null;
  onSelectSheet: (sheet: Sd844TemplateSheetPreview) => void;
  onChangeDataStartRow: (row: number) => void;
  readOnly: boolean;
}) {
  return (
    // items-start, not items-end: the Select mounts a hidden input beside its
    // trigger, so bottom-aligning lifts the "Sheet" label above its neighbour.
    <div className="flex flex-wrap items-start gap-3">
      <div className="space-y-1">
        <Label className="text-xs">
          <T context="label for which worksheet receives the 844 claim rows">
            Sheet
          </T>
        </Label>
        {/* Choosable only while binding a fresh upload: switching sheets resets
            every binding, so a saved template shows it read-only. */}
        {pendingUpload ? (
          <Select
            value={form.sheetName}
            onValueChange={(name) => {
              const sheet = pendingUpload.sheets.find((s) => s.name === name);
              if (sheet) onSelectSheet(sheet);
            }}
          >
            <SelectTrigger className="h-8 w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pendingUpload.sheets.map((sheet) => (
                <SelectItem key={sheet.name} value={sheet.name}>
                  {sheet.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input className="h-8 w-56" value={form.sheetName} disabled />
        )}
      </div>

      <div className="space-y-1">
        <Label className="text-xs">
          <T context="label for the first spreadsheet row that claim data is written to">
            First data row
          </T>
        </Label>
        <Input
          type="number"
          min={1}
          className="h-8 w-24"
          disabled={readOnly}
          value={form.dataStartRow}
          onChange={(e) =>
            // `min` on the input does not stop a typed negative or fractional
            // value from reaching the draft, and Save would stay enabled.
            onChangeDataStartRow(
              Math.max(1, Math.floor(e.target.valueAsNumber || 1)),
            )
          }
        />
      </div>
    </div>
  );
}

/** Save / test download / revert. */
function TemplateActions({
  form,
  editor,
}: {
  form: TemplateDraft;
  editor: Sd844TemplateEditorController;
}) {
  const hasTemplate = !!editor.template;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {editor.canEdit && (
        <SaveButton
          onSave={() => void editor.save()}
          isSaving={editor.isSaving}
          // Nothing to save on an untouched saved template.
          disabled={
            editor.isBusy ||
            !form.sheetName ||
            (!editor.hasUnsavedEdits && hasTemplate)
          }
        />
      )}
      <TestDownloadButton
        onClick={() => void editor.preview()}
        isPreviewing={editor.isPreviewing}
        disabled={editor.isBusy}
      />
      {editor.hasUnsavedEdits && <UnsavedNotice />}
      {editor.canEdit && hasTemplate && (
        <RevertButton
          onClick={() => void editor.remove()}
          isDeleting={editor.isDeleting}
          disabled={editor.isBusy}
        />
      )}
    </div>
  );
}

function SaveButton({
  onSave,
  isSaving,
  disabled,
}: {
  onSave: () => void;
  isSaving: boolean;
  disabled: boolean;
}) {
  return (
    <Button type="button" size="sm" onClick={onSave} disabled={disabled}>
      {isSaving && <SpinnerIcon className="size-4 animate-spin" />}
      <T context="button that saves the supplier 844 template mapping">
        Save Template
      </T>
    </Button>
  );
}

/**
 * Renders the form AS ON SCREEN, saved or not — checking a binding must not
 * require making it live first.
 */
function TestDownloadButton({
  onClick,
  isPreviewing,
  disabled,
}: {
  onClick: () => void;
  isPreviewing: boolean;
  disabled: boolean;
}) {
  const gt = useGT();
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={onClick}
      disabled={disabled}
      title={gt(
        'Download the supplier form filled with real transactions, using the mapping shown here.',
        { context: 'tooltip for the 844 template test download button' },
      )}
    >
      {isPreviewing ? (
        <SpinnerIcon className="size-4 animate-spin" />
      ) : (
        <DownloadSimpleIcon className="size-4" />
      )}
      <T context="button that downloads a test 844 file built from the template">
        Test Download
      </T>
    </Button>
  );
}

function UnsavedNotice() {
  return (
    <span className="text-xs text-amber-700 dark:text-amber-400">
      <T context="notice that 844 template changes are not live yet">
        Unsaved changes — claims still go out on the saved version.
      </T>
    </span>
  );
}

function RevertButton({
  onClick,
  isDeleting,
  disabled,
}: {
  onClick: () => void;
  isDeleting: boolean;
  disabled: boolean;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="text-destructive ml-auto"
      onClick={onClick}
      disabled={disabled}
    >
      {isDeleting ? (
        <SpinnerIcon className="size-4 animate-spin" />
      ) : (
        <TrashIcon className="size-4" />
      )}
      <T context="button that removes a supplier 844 template, reverting to the default">
        Revert to Default
      </T>
    </Button>
  );
}

/**
 * The workbook's other sheets, each with a "send to supplier" choice.
 *
 * The team's forms carry an "Example" tab — our own layout with a real claim
 * line in it — which is pre-ticked for removal.
 */
function ExcludedSheets({
  claimSheet,
  sheetNames,
  excluded,
  onToggle,
  readOnly,
}: {
  claimSheet: string;
  sheetNames: string[];
  excluded: string[];
  onToggle: (sheetName: string, excluded: boolean) => void;
  readOnly: boolean;
}) {
  if (sheetNames.length === 0) return null;
  const excludedSet = new Set(excluded);

  return (
    <div className="space-y-1">
      <Label className="text-xs">
        <T context="label for choosing which other worksheets are removed before sending">
          Other sheets in this file
        </T>
      </Label>
      <p className="text-muted-foreground text-xs">
        <T context="explains excluding worksheets from the 844 file sent to a supplier">
          Claim lines go on <Var>{claimSheet}</Var>. Tick any sheet that should
          be removed before the file is sent to the supplier.
        </T>
      </p>
      <div className="flex flex-wrap gap-4">
        {sheetNames.map((name) => (
          <label key={name} className="flex items-center gap-2 text-xs">
            <Checkbox
              checked={excludedSet.has(name)}
              disabled={readOnly}
              onCheckedChange={(checked) => onToggle(name, checked === true)}
            />
            <T context="checkbox: remove a named worksheet from the supplier's 844 file">
              Remove “<Var>{name}</Var>”
            </T>
          </label>
        ))}
      </div>
    </div>
  );
}

/** Which real lines feed the sample values and the test download. */
function SampleSourcePicker({
  source,
  onChange,
  invoices,
  isLoading,
}: {
  source: Sd844TemplateSampleSource;
  onChange: (source: Sd844TemplateSampleSource) => void;
  invoices: string[] | null;
  isLoading: boolean;
}) {
  const gt = useGT();

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <Label className="text-xs">
        <T context="label for which real transactions the 844 template test uses">
          Test with
        </T>
      </Label>
      <Select
        value={source}
        onValueChange={(value) =>
          onChange(value === 'sent' ? 'sent' : 'staged')
        }
      >
        <SelectTrigger className="h-8 w-56">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="staged">
            {gt('Latest staged transactions', {
              context: 'sample source option: newest staged 844 lines',
            })}
          </SelectItem>
          <SelectItem value="sent">
            {gt('Latest sent claim (with VDM)', {
              context: 'sample source option: newest sent 844 email claim',
            })}
          </SelectItem>
        </SelectContent>
      </Select>
      {isLoading ? (
        <SpinnerIcon className="text-muted-foreground size-3.5 animate-spin" />
      ) : invoices === null ? (
        <span className="text-muted-foreground">
          <T context="shown when a supplier has no transactions to sample">
            No transactions of this kind for this supplier yet.
          </T>
        </span>
      ) : (
        <span className="text-muted-foreground">
          <T context="names the invoice a sample value came from">
            Sample values from invoice <Var>{invoices[0] ?? ''}</Var>
          </T>
        </span>
      )}
    </div>
  );
}

/**
 * One line saying what was filled in automatically and what still needs a human.
 *
 * The bindings are pre-filled, so without this the form looks finished. Naming
 * the counts is what turns "already done" into "review these".
 */
function SuggestionSummary({
  isSuggesting,
  suggestedCount,
  unresolvedCount,
  uncertainCount,
}: {
  isSuggesting: boolean;
  suggestedCount: number;
  unresolvedCount: number;
  uncertainCount: number;
}) {
  if (isSuggesting) {
    return (
      <p className="text-muted-foreground text-xs">
        <T context="status while the 844 template column matcher runs">
          Matching the supplier&apos;s columns to our fields…
        </T>
      </p>
    );
  }

  if (suggestedCount === 0 && unresolvedCount === 0 && uncertainCount === 0)
    return null;

  return (
    <div className="space-y-0.5 text-xs">
      <p>
        <T context="summary of automatic 844 template column matching results">
          <Var>{suggestedCount}</Var> column(s) matched automatically —{' '}
          <strong>check them before saving</strong>.{' '}
          <Var>{unresolvedCount}</Var> still need a source.
        </T>
      </p>
      {uncertainCount > 0 && (
        <p className="text-amber-700 dark:text-amber-400">
          <T context="warning that some 844 template matches are not certain">
            <Var>{uncertainCount}</Var> of them are not an exact match and are
            marked <strong>Check this match</strong> — confirm or change each
            one.
          </T>
        </p>
      )}
    </div>
  );
}
